import { useQuery } from 'react-query';

import { getRisk as queryFn } from 'api/endPoints/getRisk';
import { QueryKeys } from 'api/queryKeys';

import { IAssignee } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/battlecards.types';

export const useQueryGetRisk = (params: QueryParams) => {
    return useQuery<QueryFnResponse, QueryError, QueryResponse, [QueryKeys, unknown]>({
        queryKey: ['getRisk', params],
        queryFn: () => queryFn(params),
        select: (data) => {
            const activitiesByKey: QueryResponse['activitiesByKey'] = {
                wbs: null,
                activityCodes: null,
                cluster: null,
                specific: null,
                segment: null,
                all: null,
            };

            let linkOption: QueryResponse['linkOption'] = 'all';
            const wbs = data.segment.filter.filters.find((item) => item.name === 'WBS_SEARCH');
            const activityCodes = data.segment.filter.filters.find((item) => item.name === 'ACTIVITY_CODES');
            const cluster = data.segment.filter.filters.find((item) => item.name === 'CLUSTER_CARD_BOARD');
            const specific = data.segment.filter.filters.find((item) => item.name === 'CUSTOM_ACTIVITIES_SET');
            const segment = data.segment.userGenerated ? data.segment.segmentId : null;

            if (segment) {
                activitiesByKey.segment = segment;
                linkOption = 'segment';
            } else if (wbs) {
                activitiesByKey.wbs = Number(wbs.data[0]);
                linkOption = 'wbs';
            } else if (activityCodes) {
                activitiesByKey.activityCodes = activityCodes.data.map(Number);
                linkOption = 'activityCodes';
            } else if (cluster) {
                activitiesByKey.cluster = Number(cluster.data[0]);
                linkOption = 'cluster';
            } else if (specific) {
                activitiesByKey.specific = specific.data;
                linkOption = 'specific';
            }

            return {
                id: data.riskId,
                cardId: data.cardId,
                uid: data.riskUId,
                name: data.title,
                status: data.status.name,
                probability: data.probability.name,
                impactType: data.impactType.key,
                distribution: data.distribution.name,
                distributionRange: {
                    min: data.distributionRange.min,
                    middle: data.distributionRange.middle,
                    max: data.distributionRange.max,
                },
                assignee: data.assignee.userName,
                assigneeValue: {
                    assignee: data.assignee,
                    id: null,
                    label: `${data.assignee.firstName} ${data.assignee.lastName}`,
                    value: data.assignee.userName,
                },
                linkedActivities: 0,
                description: data.details,
                contractId: data.contract.id,
                activitiesByKey: activitiesByKey,
                linkOption: linkOption,
                tags: data.tags,
            };
        },
    });
};

export interface QueryParams {
    riskId: string;
}

export type QueryResponse = {
    id: number;
    cardId: number;
    uid: string;
    name: string;
    status: 'NEW' | 'OPEN' | 'CLOSED';
    probability: 'VERY_LOW' | 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH' | 'CERTAIN';
    impactType: 'RELATIVE' | 'ABSOLUTE';
    distribution: 'UNIFORM' | 'TRIANGLE' | 'BETA_PERT';
    distributionRange: {
        min: number;
        middle: number | null;
        max: number;
    };
    assignee: string;
    assigneeValue: ISelectOption<string> & { assignee: IAssignee };
    linkedActivities: number;
    description: string;
    contractId: number;
    linkOption: 'wbs' | 'activityCodes' | 'cluster' | 'specific' | 'segment' | 'all';
    activitiesByKey: {
        wbs: number | null;
        activityCodes: number[] | null;
        cluster: number | null;
        specific: string[] | null;
        segment: number | null;
        all: null;
    };
    tags: { name: string }[];
};

interface QueryFnResponse {
    assignee: IAssignee;
    cardId: number;
    contract: { id: number; name: string };
    details: string;
    distribution: { id: number; name: QueryResponse['distribution'] };
    distributionRange: { min: number; middle: number | null; max: number };
    impactType: { key: QueryResponse['impactType']; value: '%' | 'Days' };
    probability: { id: number; name: QueryResponse['probability']; value: number };
    project: { id: number; name: string };
    riskId: number;
    riskUId: string;
    status: { id: number; name: QueryResponse['status']; label: string };
    title: string;
    type: { id: number; name: 'RISK_EVENT'; label: 'Risk Event' };
    segment: {
        segmentId: number;
        contractId: number;
        name: string;
        filter: {
            filters: {
                name: string;
                data: string[];
                operation: null;
            }[];
        };
        entireProject: false;
        userGenerated: boolean;
    };
    tags: { name: string }[];
}
